import api from "lib/api";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import RolesForm from "../RolesForm";
import { Loading, SearchSelectOptionProps } from "@alm/flow-ds/components";
import { RoleName, RoleDescription, MembersPanel, DeleteRolePanel } from "..";
import { createErrorAlert } from "lib/api/errors";
import store from "store";
import { createAlertAction } from "data/app/appActions";
import axios from "axios";

export default () => {
	const { t } = useTranslation();
	const history = useHistory();
	const params: { role: string } = useParams();

	const [alert, setAlert] = useState<string | undefined>(undefined);

	const [init, setInit] = useState(false);
	const [nameLoading, setNameLoading] = useState(false);
	const [descLoading, setDescLoading] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [systemRole, setSystemRole] = useState<boolean>(false);

	// State for form inputs
	const [members, setMembers] = useState<SearchSelectOptionProps[]>([]);
	const [managers, setManagers] = useState<SearchSelectOptionProps[]>([]);
	const [name, setName] = useState<string>("");
	const [description, setDescription] = useState<string | undefined>(
		undefined
	);

	const formatMemberOptions = useCallback(
		members =>
			members.map((user: string) => {
				return { label: user, value: user };
			}),
		[]
	);
	const formatManagerOptions = useCallback(
		managers =>
			managers.map((manager: string) => {
				return { label: manager, value: manager };
			}),
		[]
	);

	// Getting the role data
	const getRoleData = useCallback(async () => {
		try {
			const { name, description, users, managers, type } =
				await api.roles.getSingleRole(params.role);
			// Set state for form inputs from response
			setName(name);
			setDescription(description);
			setSystemRole(type === "system");
			setMembers(formatMemberOptions(users));
			setManagers(formatManagerOptions(managers));
			setInit(true);
		} catch (error) {
			console.error(error);
			// Redirect to roles page if the role does not exist
			// and send error message to be displayed on roles page
			history.push({
				pathname: "/admin/roles",
			});
		}
	}, [formatManagerOptions, formatMemberOptions, history, params.role]);

	/**
	 * Adding a member to a role
	 *
	 * @param e {value: string; label: string}
	 */
	const handleAddMember = useCallback(
		async e => {
			try {
				await api.roles.addUserToRole(
					{
						role: params.role,
						username: e.value,
					},
					{ handleErrors: true }
				);

				const { users } = await api.roles.getSingleRole(params.role);
				setMembers(formatMemberOptions(users));
			} catch (error) {
				console.error(error);
				if (error && axios.isAxiosError(error))
					store.dispatch(
						createAlertAction(
							createErrorAlert(error, "errorAddingMember")
						)
					);
			}
		},
		[formatMemberOptions, params.role]
	);

	/**
	 * Adding a manager to a role
	 *
	 * @param username {value: string; label: string}
	 */
	const handleAddManager = useCallback(
		async username => {
			try {
				await api.roles.addManagerToRole(
					{
						role: params.role,
						username: username.value,
					},
					{ handleErrors: true }
				);

				const { managers } = await api.roles.getSingleRole(params.role);
				setManagers(formatManagerOptions(managers));
			} catch (error) {
				console.error(error);
				if (error && axios.isAxiosError(error))
					store.dispatch(
						createAlertAction(
							createErrorAlert(error, "errorAddingManager")
						)
					);
			}
		},
		[formatManagerOptions, params.role]
	);

	/**
	 * Removing a member from a role
	 *
	 * @username string of member to be removed
	 */
	const handleRemoveMember = useCallback(
		async username => {
			try {
				await api.roles.removeUserFromRole(
					{
						role: params.role,
						username,
					},
					{ handleErrors: true }
				);

				const { users } = await api.roles.getSingleRole(params.role);
				setMembers(formatMemberOptions(users));
			} catch (error) {
				console.error(error);
				if (error && axios.isAxiosError(error))
					store.dispatch(
						createAlertAction(
							createErrorAlert(error, "errorRemovingMember")
						)
					);
			}
		},
		[formatMemberOptions, params.role]
	);

	/**
	 * Removing a manager from a role
	 *
	 * @username string of manager to be removed
	 */
	const handleRemoveManager = useCallback(
		async username => {
			try {
				await api.roles.removeManagerFromRole(
					{
						role: params.role,
						username,
					},
					{ handleErrors: true }
				);

				const { managers } = await api.roles.getSingleRole(params.role);
				setManagers(formatManagerOptions(managers));
			} catch (error) {
				console.error(error);
				if (error && axios.isAxiosError(error))
					store.dispatch(
						createAlertAction(
							createErrorAlert(error, "errorRemovingManager")
						)
					);
			}
		},
		[formatManagerOptions, params.role]
	);

	/**
	 * Updating the role name or description
	 *
	 * @param data will either include name or description
	 */
	const handleUpdateRole = useCallback(
		async (data: { name?: string; description?: string }) => {
			// Remove whitespace from inputs
			const trimName = data?.name?.trim();
			const trimDesc = data?.description?.trim();
			trimName ? setNameLoading(true) : setDescLoading(true);
			try {
				await api.roles.updateRole(
					{
						role: params.role,
						...(trimName ? { name: trimName } : {}),
						...(trimDesc
							? { description: trimDesc }
							: data.description
							? {
									description: t(
										"main.admin.text.noDescription"
									),
							  }
							: {}),
					},
					{ handleErrors: true }
				);
				trimName && (params.role = trimName);
			} catch (error) {
				console.error(error);
				if (error && axios.isAxiosError(error))
					store.dispatch(
						createAlertAction(
							createErrorAlert(error, "errorUpdatingRole")
						)
					);
			}
			trimName ? setNameLoading(false) : setDescLoading(false);
		},
		[params, t]
	);

	// Deleting a role
	const handleDeleteRole = useCallback(async () => {
		setDeleteLoading(true);
		try {
			await api.roles.deleteRole(params.role, { handleErrors: true });
			// Redirect to roles page if successsful
			history.push("/admin/roles");
		} catch (error) {
			setDeleteLoading(false);
			console.error(error);
			if (error && axios.isAxiosError(error))
				store.dispatch(
					createAlertAction(
						createErrorAlert(error, "errorDeletingRole")
					)
				);
		}
	}, [history, params.role]);

	useEffect(() => {
		if (!init) getRoleData();
	}, [getRoleData, init]);

	if (!init) return <Loading className="elem__loader--md" />;

	return (
		<RolesForm alert={alert} setAlert={setAlert} edit={true}>
			<RoleName
				loading={nameLoading}
				name={name}
				setName={setName}
				alert={alert === "missingRoleName"}
				onUpdateRole={handleUpdateRole}
				systemRole={systemRole}
			/>
			<RoleDescription
				description={description}
				setDescription={setDescription}
				onUpdateRole={handleUpdateRole}
				loading={descLoading}
				systemRole={systemRole}
			/>
			<MembersPanel
				onAddMember={handleAddMember}
				onRemoveMember={handleRemoveMember}
				members={members}
				setMembers={setMembers}
				type="members"
			/>
			<MembersPanel
				onAddMember={handleAddManager}
				onRemoveMember={handleRemoveManager}
				members={managers}
				setMembers={setManagers}
				type="managers"
			/>
			<DeleteRolePanel
				onDeleteRole={handleDeleteRole}
				loading={deleteLoading}
				systemRole={systemRole}
			/>
		</RolesForm>
	);
};
